import { User } from 'firebase/auth'

import { generateApiPath } from '@taxfix/scouts-helpers'

import { CompanyReturnDTO, CompanyReturnPriceDTO, CompanyReturnStatusType, UserAgreementDTO } from '@/models'
import { CompanyDTO } from '@/models/company'

import { apiSlice } from '../api-slice'
import { BaseCompanyParams, BaseCompanyReturnParams } from '../company-shared/models'
import { companyPrePaywallBaseQuery } from '../company-shared/query'
import { ENDPOINTS } from '../endpoints'
import { Tags } from '../tags'
import { handleCompanyReturnPriceValidation } from './companyReturnPriceErrorValidation'

export interface CreateCompanyReturnParams extends BaseCompanyParams {
    accountingPeriodEnd?: CompanyReturnDTO['accountingPeriodEnd']
    accountingPeriodStart?: CompanyReturnDTO['accountingPeriodStart']
    user: User | null
}

export interface GetCompanyReturnsParams extends BaseCompanyParams {
    user: User | null
}

export interface GetCompanyReturnParams extends BaseCompanyReturnParams {
    user: User | null
}

export interface UpdateCompanyReturnParams extends BaseCompanyReturnParams, Omit<CompanyReturnDTO, 'companyId' | 'id'> {
    user: User | null
}

export interface UpdateCompanyReturnDatesParams extends GetCompanyReturnParams {
    accountingPeriodStart: CompanyReturnDTO['accountingPeriodStart']
    accountingPeriodEnd: CompanyReturnDTO['accountingPeriodEnd']
}

export interface UpdateCompanyReturnStatusParams extends BaseCompanyReturnParams {
    newStatus: CompanyReturnStatusType
}

export interface AssignCompanyReturnParams extends BaseCompanyReturnParams {
    user: User
}

const extendedApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        createCompanyReturn: builder.mutation<CompanyReturnDTO, CreateCompanyReturnParams>({
            queryFn: async ({ companyId, user, ...payload }, api, extraOptions, baseQuery) => {
                const response = await companyPrePaywallBaseQuery({ baseQuery, user })(
                    {
                        method: 'POST',
                        url: generateApiPath(ENDPOINTS.COMPANY_RETURNS.POST.CREATE_RETURN, { companyId }),
                        ...(user ? { data: payload } : { body: payload }), // If user is authenticated we use the axios baseQuery but if ghost user we use the fetchBaseQuery
                    },
                    api,
                    extraOptions
                )

                if (response.error) return { error: response.error }

                return { data: response.data as CompanyReturnDTO }
            },
            invalidatesTags: (result, error) => ((result && !('id' in result)) || error ? [] : [Tags.CompanyReturns]),
        }),
        getCompanyReturns: builder.query<CompanyReturnDTO[], GetCompanyReturnsParams>({
            queryFn: async ({ companyId, user }, api, extraOptions, baseQuery) => {
                const response = await companyPrePaywallBaseQuery({ baseQuery, user })(
                    {
                        method: 'GET',
                        url: generateApiPath(ENDPOINTS.COMPANY_RETURNS.GET.LIST, { companyId }),
                    },
                    api,
                    extraOptions
                )

                if (response.error) return { error: response.error }

                return { data: response.data as CompanyReturnDTO[] }
            },
            providesTags: (result: CompanyReturnDTO[] | undefined) =>
                result
                    ? [...result.map(({ id }) => ({ type: Tags.CompanyReturns, id })), Tags.CompanyReturns]
                    : [Tags.CompanyReturns],
        }),
        // Fetches the company returns for all companies
        getAllCompanyReturns: builder.query<CompanyReturnDTO[], { user: User | null }>({
            queryFn: async ({ user }, api, extraOptions, baseQuery) => {
                const companiesResponse = await companyPrePaywallBaseQuery({ baseQuery, user })(
                    {
                        method: 'GET',
                        url: generateApiPath(ENDPOINTS.COMPANIES.GET.LIST),
                    },
                    api,
                    extraOptions
                )

                if (companiesResponse.error) return { error: companiesResponse.error }

                const companies = companiesResponse.data as CompanyDTO[]
                const allCompanyReturns = await companies?.reduce<Promise<CompanyReturnDTO[]>>(async (acc, curr) => {
                    const result = await acc
                    const response = await companyPrePaywallBaseQuery({ baseQuery, user })(
                        {
                            method: 'GET',
                            url: generateApiPath(ENDPOINTS.COMPANY_RETURNS.GET.LIST, { companyId: curr.id }),
                        },
                        api,
                        extraOptions
                    )

                    if (!response || response.error || !response.data) return result

                    return [...result, ...(response.data as CompanyReturnDTO[])]
                }, Promise.resolve([]))

                return { data: allCompanyReturns }
            },
            providesTags: (result: CompanyReturnDTO[] | undefined) =>
                result
                    ? [...result.map(({ id }) => ({ type: Tags.CompanyReturns, id })), Tags.CompanyReturns]
                    : [Tags.CompanyReturns],
        }),
        getCompanyReturn: builder.query<CompanyReturnDTO, GetCompanyReturnParams>({
            queryFn: async ({ companyId, companyReturnId, user }, api, extraOptions, baseQuery) => {
                const response = await companyPrePaywallBaseQuery({ baseQuery, user })(
                    {
                        method: 'GET',
                        url: generateApiPath(ENDPOINTS.COMPANY_RETURNS.GET.SINGLE, { companyId, companyReturnId }),
                    },
                    api,
                    extraOptions
                )

                if (response.error) return { error: response.error }

                return { data: response.data as CompanyReturnDTO }
            },
            providesTags: (_1, _2, arg) => [{ type: Tags.CompanyReturns, id: arg.companyReturnId }],
        }),
        updateCompanyReturn: builder.mutation<CompanyReturnDTO, UpdateCompanyReturnParams>({
            queryFn: async ({ companyId, companyReturnId, user, ...params }, api, extraOptions, baseQuery) => {
                const response = await companyPrePaywallBaseQuery({ baseQuery, user })(
                    {
                        method: 'PUT',
                        url: generateApiPath(ENDPOINTS.COMPANY_RETURNS.PUT.UPDATE_RETURN, {
                            companyId,
                            companyReturnId,
                        }),
                        ...(user ? { data: params } : { body: params }), // If user is authenticated we use the axios baseQuery but if ghost user we use the fetchBaseQuery
                    },
                    api,
                    extraOptions
                )

                if (response.error) return { error: response.error }

                return { data: response.data as CompanyReturnDTO }
            },
            invalidatesTags: (_1, _2, arg) => [{ type: Tags.CompanyReturns, id: arg.companyReturnId }],
        }),
        getCompanyReturnPrice: builder.query<CompanyReturnPriceDTO, BaseCompanyReturnParams>({
            queryFn: async (params, api, _extraOptions, baseQuery) => {
                const response = await baseQuery({
                    method: 'GET',
                    url: generateApiPath(ENDPOINTS.COMPANY_RETURNS.GET.PRICE, params),
                })

                const companyReturnPrice = response.data as CompanyReturnPriceDTO

                // @TODO: Remove once validation is handled via error response
                if (response.error) {
                    handleCompanyReturnPriceValidation(companyReturnPrice)

                    return { error: response.error }
                }

                // If the price is 0, BE may have updated the companyReturn.hasPaid property
                if (companyReturnPrice.price === 0) {
                    api.dispatch(invalidateCompanyReturn(params.companyReturnId))
                }

                return { data: companyReturnPrice }
            },
            providesTags: (_1, _2, arg) => [{ type: Tags.CompanyReturnPrice, id: arg.companyReturnId }],
        }),

        // These endpoints will be used when user is authenticated (post-paywall) hence the "query" (instead of "queryFn")

        assignCompanyReturn: builder.mutation<CompanyReturnDTO, BaseCompanyReturnParams>({
            query: (params) => ({
                method: 'POST',
                url: generateApiPath(ENDPOINTS.COMPANY_RETURNS.POST.ASSIGN_ACCOUNTANT, params),
            }),
            invalidatesTags: (_1, _2, arg) => [{ type: Tags.CompanyReturns, id: arg.companyReturnId }],
        }),
        updateCompanyReturnStatus: builder.mutation<CompanyReturnDTO, UpdateCompanyReturnStatusParams>({
            query: ({ companyId, companyReturnId, newStatus }) => ({
                method: 'PUT',
                url: generateApiPath(ENDPOINTS.COMPANY_RETURNS.PUT.UPDATE_STATUS, {
                    companyId,
                    companyReturnId,
                    newStatus,
                }),
            }),
            invalidatesTags: (_result, error, arg) => {
                return error ? [] : [{ type: Tags.CompanyReturns, id: arg.companyReturnId }]
            },
        }),
        getCompanyReturnAgreement: builder.query<UserAgreementDTO, BaseCompanyReturnParams>({
            query: ({ companyId, companyReturnId }) => ({
                method: 'GET',
                url: generateApiPath(ENDPOINTS.COMPANY_RETURNS.GET.AGREEMENT, {
                    companyId,
                    companyReturnId,
                }),
            }),
        }),
        updateCompanyReturnAccountingPeriodDates: builder.mutation<CompanyReturnDTO, UpdateCompanyReturnDatesParams>({
            queryFn: async ({ companyId, companyReturnId, user, ...params }, api, extraOptions, baseQuery) => {
                const response = await companyPrePaywallBaseQuery({ baseQuery, user })(
                    {
                        method: 'PUT',
                        url: generateApiPath(ENDPOINTS.COMPANY_RETURNS.PUT.UPDATE_DATES, {
                            companyId,
                            companyReturnId,
                        }),
                        ...(user ? { data: params } : { body: params }), // If user is authenticated we use the axios baseQuery but if ghost user we use the fetchBaseQuery
                    },
                    api,
                    extraOptions
                )

                if (response.error) return { error: response.error }

                return { data: response.data as CompanyReturnDTO }
            },
            invalidatesTags: (_1, _2, arg) => [{ type: Tags.CompanyReturns, id: arg.companyReturnId }],
        }),
        deleteCompanyReturn: builder.mutation<void, BaseCompanyReturnParams>({
            query: ({ companyId, companyReturnId }) => ({
                method: 'DELETE',
                url: generateApiPath(ENDPOINTS.COMPANY_RETURNS.DELETE.SINGLE, {
                    companyId,
                    companyReturnId,
                }),
            }),
            invalidatesTags: [Tags.Companies, Tags.CompanyReturns],
        }),
    }),
})

export const {
    useCreateCompanyReturnMutation,
    useGetCompanyReturnQuery,
    useLazyGetCompanyReturnQuery,
    useGetCompanyReturnsQuery,
    useLazyGetCompanyReturnsQuery,
    useGetAllCompanyReturnsQuery,
    useUpdateCompanyReturnMutation,
    useAssignCompanyReturnMutation,
    useUpdateCompanyReturnStatusMutation,
    useGetCompanyReturnPriceQuery,
    useLazyGetCompanyReturnPriceQuery,
    useGetCompanyReturnAgreementQuery,
    useUpdateCompanyReturnAccountingPeriodDatesMutation,
    useDeleteCompanyReturnMutation,
} = extendedApi

const invalidateCompanyReturn = (companyReturnId: BaseCompanyReturnParams['companyReturnId']) =>
    extendedApi.util.invalidateTags([{ type: Tags.CompanyReturns, id: companyReturnId }])
